import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Offcanvas, Button, Row, Col, Spinner } from 'react-bootstrap';
import {
    FaGlobe,
    FaIdCard,
    FaCloud,
    FaInfoCircle,
    FaServer,
    FaList
} from 'react-icons/fa';
import { BiPencil } from "react-icons/bi";
import { BsX } from "react-icons/bs";
import DomainView from './DomainView';
import DnsList from './DnsList'; // This might need a refactor if it doesn't work as a child
import DomainForm from './DomainForm';

const DomainDetails = ({ show, onHide, domainId }) => {
    const [domainDetails, setDomainDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('dnsRecords');
    const [showEditModal, setShowEditModal] = useState(false);
    const [dnsFormOpen, setDnsFormOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const fetchDomainDetails = async () => {
        if (!domainId) return;
        setLoading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/domain/${domainId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setDomainDetails(response.data);
        } catch (error) {
            console.error('Error fetching domain details:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show && domainId) {
            fetchDomainDetails();
        }
    }, [show, domainId]);

    const handleEditDomain = async (id, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.put(`${backendUrl}/api/domain/${id}`, updatedData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setDomainDetails(response.data.Data);
            setShowEditModal(false);
            return {};
        } catch (error) {
            if (error.response?.data?.errors) {
                return error.response.data.errors;
            }
            return { general: 'Failed to update domain' };
        }
    };

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    return (
        <>
            <Offcanvas
                show={show && !showEditModal}
                onHide={onHide}
                placement="end"
                style={{ width: '1000px', zIndex: 1045 }}
                className={`Domain-offcanvas ${dnsFormOpen ? 'd-none' : ''}`}
            >
                <Offcanvas.Header className="border-bottom bg-white py-3">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center">
                            <div className="bg-primary rounded-circle p-2 me-3">
                                <FaGlobe className="text-white" size={18} />
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold text-dark">{domainDetails?.domain_name || 'Loading...'}</h5>
                                <small className="text-muted">Domain ID: {domainId}</small>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setShowEditModal(true)}
                            >
                                <BiPencil className="me-1" />
                                Edit Domain
                            </Button>
                            <Button
                                variant="light"
                                size="sm"
                                onClick={onHide}
                                className="border-0"
                            >
                                <BsX size={20} />
                            </Button>
                        </div>
                    </div>
                </Offcanvas.Header>

                <Offcanvas.Body className="p-0">
                    <div className="p-4 border-bottom bg-light">
                        <div className="row g-4">
                            <Col md={6}>
                                <div className="d-flex align-items-center">
                                    <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                        <FaIdCard className="text-primary" size={16} />
                                    </div>
                                    <div>
                                        <label className="text-muted small mb-1 d-block">Zone ID</label>
                                        <h6 className="mb-0 fw-semibold text-dark">
                                            {domainDetails?.zoneid || 'Not assigned'}
                                        </h6>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="d-flex align-items-center">
                                    <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                        <FaCloud className="text-primary" size={16} />
                                    </div>
                                    <div>
                                        <label className="text-muted small mb-1 d-block">Provider</label>
                                        <h6 className="mb-0 fw-semibold text-dark">
                                            {domainDetails?.provider || 'Not specified'}
                                        </h6>
                                    </div>
                                </div>
                            </Col>
                        </div>
                    </div>

                    <div className="border-bottom bg-white">
                        <ul className="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'dnsRecords' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('dnsRecords')}
                                    role="tab"
                                >
                                    <FaList className="me-2" size={14} />
                                    DNS Records
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'domainDetails' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('domainDetails')}
                                    role="tab"
                                >
                                    <FaInfoCircle className="me-2" size={14} />
                                    Domain Details
                                </button>
                            </li>
                        </ul>
                    </div>

                    <div className="tab-content" style={{ minHeight: '500px' }}>
                        {activeTab === "dnsRecords" && domainId && (
                            <div className="tab-pane active">
                                <DnsList 
                                    domainId={domainId} 
                                    domainName={domainDetails?.domain_name}
                                    zoneId={domainDetails?.zoneid}
                                    instanceIp={domainDetails?.tenant?.instance?.ip_address}
                                    isTabMode={true} // New prop to tell DnsList it's inside DomainDetails
                                    onFormToggle={(isOpen) => setDnsFormOpen(isOpen)}
                                />
                            </div>
                        )}
                        {activeTab === "domainDetails" && (
                            <div className="tab-pane active">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                    </div>
                                ) : (
                                    <DomainView domainData={domainDetails} />
                                )}
                            </div>
                        )}
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {showEditModal && (
                <DomainForm
                    showModal={showEditModal}
                    handleClose={() => setShowEditModal(false)}
                    domainId={domainId}
                    domainData={domainDetails}
                    editDomain={handleEditDomain}
                    domains={[]} // Passing empty array as we don't need validation list here usually if it's the current one
                />
            )}
        </>
    );
};

export default DomainDetails;
